/**
 * Service Container - Comprehensive Dependency Injection System
 *
 * This service container provides:
 * - Lazy service instantiation to break circular dependencies
 * - Type-safe service registration and retrieval
 * - Singleton pattern for service instances
 * - Dependency injection for complex service hierarchies
 * - Runtime context integration for store/router access
 */

import { Store } from "vuex";
import VueRouter from "vue-router";

// Service factory function type
export type ServiceFactory<T = any> = () => T;

// Service registration interface
export interface IServiceRegistration<T = any> {
  factory: ServiceFactory<T>;
  singleton: boolean;
  instance?: T;
}

// Service container interface
export interface IServiceContainer {
  register<T>(
    key: string,
    factory: ServiceFactory<T>,
    singleton?: boolean
  ): void;
  get<T>(key: string): T;
  has(key: string): boolean;
  clear(): void;
  setRuntimeContext(store: Store<any>, router: VueRouter): void;
}

/**
 * Service Container Implementation
 * Manages service registration, instantiation, and dependency injection
 */
export class ServiceContainer implements IServiceContainer {
  private static instance: ServiceContainer;
  private services: Map<string, IServiceRegistration> = new Map();
  private runtimeStore: Store<any> | null = null;
  private runtimeRouter: VueRouter | null = null;

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get the singleton instance of the service container
   */
  public static getInstance(): ServiceContainer {
    if (!ServiceContainer.instance) {
      ServiceContainer.instance = new ServiceContainer();
    }
    return ServiceContainer.instance;
  }

  /**
   * Register a service with the container
   * @param key - Unique service identifier
   * @param factory - Function that creates the service instance
   * @param singleton - Whether to cache the instance (default: true)
   */
  public register<T>(
    key: string,
    factory: ServiceFactory<T>,
    singleton: boolean = true
  ): void {
    this.services.set(key, {
      factory,
      singleton,
      instance: undefined,
    });

    console.log(
      `[ServiceContainer] Registered service: ${key} (singleton: ${singleton})`
    );
  }

  /**
   * Get a service instance from the container
   * @param key - Service identifier
   * @returns Service instance
   */
  public get<T>(key: string): T {
    const registration = this.services.get(key);

    if (!registration) {
      throw new Error(`[ServiceContainer] Service not found: ${key}`);
    }

    // Return cached instance if singleton and already instantiated
    if (registration.singleton && registration.instance) {
      return registration.instance as T;
    }

    // Create new instance
    try {
      const instance = registration.factory();

      // Cache instance if singleton
      if (registration.singleton) {
        registration.instance = instance;
      }

      console.log(`[ServiceContainer] Instantiated service: ${key}`);
      return instance as T;
    } catch (error) {
      console.error(
        `[ServiceContainer] Failed to instantiate service: ${key}`,
        error
      );
      throw error;
    }
  }

  /**
   * Check if a service is registered
   * @param key - Service identifier
   * @returns True if service is registered
   */
  public has(key: string): boolean {
    return this.services.has(key);
  }

  /**
   * Clear all services (useful for testing)
   */
  public clear(): void {
    this.services.clear();
    this.runtimeStore = null;
    this.runtimeRouter = null;
    console.log("[ServiceContainer] All services cleared");
  }

  /**
   * Set runtime context for services that need store/router access
   * @param store - Vuex store instance
   * @param router - Vue router instance
   */
  public setRuntimeContext(store: Store<any>, router: VueRouter): void {
    this.runtimeStore = store;
    this.runtimeRouter = router;
    console.log("[ServiceContainer] Runtime context set");
  }

  /**
   * Get the runtime store (for services that need it)
   */
  public getStore(): Store<any> | null {
    return this.runtimeStore;
  }

  /**
   * Get the runtime router (for services that need it)
   */
  public getRouter(): VueRouter | null {
    return this.runtimeRouter;
  }

  /**
   * Get all registered service keys (useful for debugging)
   */
  public getRegisteredServices(): string[] {
    return Array.from(this.services.keys());
  }

  /**
   * Get service registration info (useful for debugging)
   */
  public getServiceInfo(key: string): IServiceRegistration | undefined {
    return this.services.get(key);
  }
}

// Convenience function to get the service container instance
export function getServiceContainer(): ServiceContainer {
  return ServiceContainer.getInstance();
}

// Service registration helper functions
export function registerService<T>(
  key: string,
  factory: ServiceFactory<T>,
  singleton: boolean = true
): void {
  getServiceContainer().register(key, factory, singleton);
}

export function getService<T>(key: string): T {
  return getServiceContainer().get<T>(key);
}

export function hasService(key: string): boolean {
  return getServiceContainer().has(key);
}

// Service keys constants for type safety
export const SERVICE_KEYS = {
  // HTTP Services
  HTTP_SERVICE: "httpService",

  // Data Services
  ATHLETE_DATA: "athleteData",
  ATHLETE_DATA_SERVICE: "athleteDataService",
  COMPETITION_DATA: "competitionData",
  CLUB_DATA: "clubData",
  CONFIG_DATA: "configData",
  BUILDER_DATA: "builderData",
  EVENT_DATA: "eventData",
  LOCATION_DATA: "locationData",
  SECONDARY_DATA: "secondaryData",
  SECONDARY_CUST_DATA: "secondaryCustData",
  SECONDARY_CUST_SERVICE: "secondaryCustService",
  CHECKIN_DATA: "checkinData",
  FIELD_HELP_DATA: "fieldHelpData",
  FIELD_HELP_SERVICE: "fieldHelpService",
  TEAM_DATA: "teamData",
  PERMISSION_DATA: "permissionData",
  COMP_EVENT_TEAM_HEADER_DATA: "compEventTeamHeaderData",

  // Business Services
  ATHLETE_SERVICE: "athleteService",
  COMPETITION_SERVICE: "competitionService",
  CONFIG_SERVICE: "configService",
  ATHLETE_COMP_SCHED_SERVICE: "athleteCompSchedService",
  SECONDARY_SERVICE: "secondaryService",
  COMMON_SERVICE: "commonService",
  TEAM_SERVICE: "teamService",
  BUILDER_SERVICE: "builderService",
  BUILDER_STORE_SERVICE: "builderStoreService",
  AGE_GROUP_SERVICE: "ageGroupService",
  COMP_RULE_SERVICE: "compRuleService",
  PERMISSIONS_SERVICE: "permissionsService",

  // Specialized Services
  ATHLETE_COMP_SCHED_DATA: "athleteCompSchedData",
  // COMP_EVENT_TEAM_HEADER_DATA: 'compEventTeamHeaderData',
  USER_PROFILE_DATA: "userProfileData",
  LOGIN_DATA: "loginData",
} as const;

export type ServiceKey = typeof SERVICE_KEYS[keyof typeof SERVICE_KEYS];
